import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { getProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CATS = ["Tous", "Spécial Enfants", "Déguisements", "Décorations", "Accessoires"];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") || "Tous";
  const [sort, setSort] = useState("default");
  const [underTen, setUnderTen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list =
      active === "Tous"
        ? products
        : active === "Spécial Enfants"
        ? products.filter((p) => p.kids)
        : products.filter((p) => p.category === active);
    if (underTen) list = list.filter((p) => p.price < 1000);
    list = [...list];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, active, sort, underTen]);

  const setCat = (c) => {
    if (c === "Tous") setParams({});
    else setParams({ cat: c });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="catalog-page">
      <div className="mb-10">
        <p className="eyebrow mb-2">Boutique Croque-Mitaine</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-stone-100">
          {active === "Tous" ? "Tout le catalogue" : active}
        </h1>
        <p className="text-stone-400 mt-3 max-w-xl">
          Préparez la nuit la plus effrayante de l'année. Stocks limités, ne tardez pas.
        </p>
      </div>

      {/* Desktop / tablet filters */}
      <div className="hidden sm:flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`filter-${c.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                active === c
                  ? "bg-amber-500 text-[#08060B] border-amber-500"
                  : "border-amber-900/40 text-stone-300 hover:border-amber-600/60 hover:text-amber-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setUnderTen((v) => !v)}
            data-testid="filter-under-10"
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all whitespace-nowrap ${
              underTen
                ? "bg-amber-500 text-[#08060B] border-amber-500"
                : "border-amber-900/40 text-stone-300 hover:border-amber-600/60 hover:text-amber-400"
            }`}
          >
            💸 Moins de 10 €
          </button>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sort-select"
            className="flex-1 sm:flex-none min-w-0 bg-[#120E1A] border border-amber-900/40 rounded-lg px-4 py-2 text-sm text-stone-200 focus:outline-none focus:border-amber-600"
          >
            <option value="default">Trier : Populaire</option>
            <option value="price-asc">Prix croissant</option>
            <option value="price-desc">Prix décroissant</option>
          </select>
        </div>
      </div>

      {/* Mobile filter trigger */}
      <div className="sm:hidden flex items-center gap-3 mb-6">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-filter-button"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-amber-900/40 text-stone-200 text-sm font-semibold"
        >
          <SlidersHorizontal className="h-4 w-4 text-amber-400" />
          Filtres
          {(active !== "Tous" || underTen) && (
            <span className="h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-[#08060B] text-xs font-bold inline-flex items-center justify-center">
              {(active !== "Tous" ? 1 : 0) + (underTen ? 1 : 0)}
            </span>
          )}
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="sort-select-mobile"
          className="min-w-0 bg-[#120E1A] border border-amber-900/40 rounded-xl px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus:border-amber-600"
        >
          <option value="default">Populaire</option>
          <option value="price-asc">Prix ↑</option>
          <option value="price-desc">Prix ↓</option>
        </select>
      </div>

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div className="sm:hidden fixed inset-0 z-[60]" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-[#120E1A] border-t border-amber-900/40 rounded-t-2xl p-5 max-h-[82vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-stone-100">Filtres</h3>
              <button onClick={() => setDrawerOpen(false)} data-testid="mobile-filter-close" aria-label="Fermer" className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center text-stone-300">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="eyebrow mb-3">Catégories</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`drawer-filter-${c.toLowerCase()}`}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                    active === c
                      ? "bg-amber-500 text-[#08060B] border-amber-500"
                      : "border-amber-900/40 text-stone-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              onClick={() => setUnderTen((v) => !v)}
              data-testid="drawer-filter-under-10"
              className={`w-full px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all mb-6 ${
                underTen ? "bg-amber-500 text-[#08060B] border-amber-500" : "border-amber-900/40 text-stone-300"
              }`}
            >
              💸 Moins de 10 €
            </button>
            <button
              onClick={() => setDrawerOpen(false)}
              data-testid="mobile-filter-apply"
              className="btn-ember w-full py-3.5 rounded-xl font-bold"
            >
              Voir {filtered.length} produit{filtered.length > 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-stone-500 py-20 text-center">Aucun produit dans cette catégorie.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
          {filtered.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
